import React, { useState } from 'react';
import type { DiaryNumericFieldSet, NumericFieldSetLabel } from './DiaryDayPage';

export interface NumericFieldSetModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** The numeric field set being displayed */
  numericFieldSet: DiaryNumericFieldSet;
  /** Current value of the activity (null if not set) */
  currentValue: number | null;
  /** Callback when a label is clicked */
  onLabelClick: (labelId: string, value: number) => void;
  /** Callback when slider value changes */
  onSliderChange: (value: number) => void;
}

/**
 * NumericFieldSetModal - Modal for selecting a value in a numeric field set
 *
 * Shows all labels for selection and a slider for intermediate values
 */
export function NumericFieldSetModal({
  isOpen,
  onClose,
  numericFieldSet,
  currentValue,
  onLabelClick,
  onSliderChange,
}: NumericFieldSetModalProps) {
  const [sliderValue, setSliderValue] = useState<number>(
    currentValue ?? numericFieldSet.minValue
  );

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Reset slider to current value
      setSliderValue(currentValue ?? numericFieldSet.minValue);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, currentValue, numericFieldSet.minValue]);

  if (!isOpen) return null;

  // Sort labels by value
  const sortedLabels = [...numericFieldSet.labels].sort((a, b) => a.value - b.value);

  // Check if current value matches a label
  const matchingLabel = currentValue !== null
    ? sortedLabels.find(l => l.value === currentValue)
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="numeric-fieldset-modal-backdrop"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 1055,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Modal content */}
        <div
          className="numeric-fieldset-modal-content"
          style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 20,
            maxWidth: 500,
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            zIndex: 1070,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              marginBottom: 16,
              paddingBottom: 12,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <h5 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              {numericFieldSet.name}
            </h5>
          </div>

          {/* Labels grid */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
              Select a value:
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: 10,
              }}
            >
              {sortedLabels.map((label) => {
                const isSelected = currentValue === label.value;
                return (
                  <button
                    key={label.id}
                    onClick={() => {
                      onLabelClick(label.id, label.value);
                      onClose();
                    }}
                    style={{
                      padding: '12px 16px',
                      border: isSelected ? '2px solid #0d6efd' : '1px solid #d1d5db',
                      borderRadius: 8,
                      backgroundColor: label.color,
                      color: getContrastColor(label.color),
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 500,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {label.name}
                    <br />
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      {label.value}{numericFieldSet.units === 'percentage' ? '%' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slider section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
              Or use slider for custom value:
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {numericFieldSet.minValue}
              </span>
              <input
                type="range"
                min={numericFieldSet.minValue}
                max={numericFieldSet.maxValue}
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value, 10))}
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {numericFieldSet.maxValue}
              </span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
                {sliderValue}{numericFieldSet.units === 'percentage' ? '%' : ''}
              </span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                onClick={() => {
                  onSliderChange(sliderValue);
                  onClose();
                }}
                style={{
                  padding: '8px 24px',
                  border: 'none',
                  borderRadius: 4,
                  backgroundColor: '#0d6efd',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Set Value
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ marginTop: 16, textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              onClick={() => {
                // To unselect, we could set to min value or handle differently
                onSliderChange(numericFieldSet.minValue);
                onClose();
              }}
              style={{
                padding: '8px 24px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 24px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Helper function to determine if text should be light or dark based on background color
 */
function getContrastColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export default NumericFieldSetModal;
